<template>
	<component :is="tag" class="file-preview" :class="{ compact: compact, large: large }">
		<button @click="$emit('remove', file)" class="close-icon">&times;</button>

		<!-- Hidden rather than removed when it cannot be shown: the load and
		-- error handlers are what decide, so the element has to exist. -->
		<img :src="file.url" :alt="file.file.name" :title="file.file.name"
		ref="uplImg" :class="{ unusable: broken }"
		@load="onImgLoad(file)" @error="onImgError"/>

		<!-- No scrim and no watermark here.
		--
		-- They were drawn on this thumbnail because it was the only picture on
		-- the page, and for a while both it and the stage drew them -- two
		-- pictures answering one slider, which is a question about which of
		-- them you are working on. The stage is the answer. This says which
		-- file is loaded. WatermarkLayer.vue still draws them, on the stage.
		-->

		<!-- Belt and braces, not the fix. The blank previews on iOS were a
		-- layout cycle, corrected in the stylesheet below. This stays
		-- because a decode can still fail: iOS does have a pixel ceiling,
		-- and a 12 MP camera original is closer to it than the 3.5 MP
		-- screenshots that were measured. Blank space is the worst way to
		-- report that, so say what the file is -- and the dimensions,
		-- because 0 x 0 here would be exactly that failure. -->
		<span v-if="broken" class="fallback">
			<!-- The glyph follows the file. A picture frame over a PDF says
				-- the image is broken, when what is true is that a document has
				-- no thumbnail: the stage beside this one has always drawn a
				-- document, and the two disagreeing is the tell. -->
			<svg v-if="isDocument" viewBox="0 0 24 24" width="26" height="26" fill="none"
			stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
			stroke-linejoin="round" aria-hidden="true">
				<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/>
				<path d="M14 3v5h5"/><path d="M9 13h6"/><path d="M9 17h4"/>
			</svg>
			<svg v-else viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor"
			stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
				<rect x="3" y="4" width="18" height="16" rx="2"/>
				<circle cx="9" cy="10" r="1.6"/><path d="m4 18 5-5 4 4 3-3 4 4"/>
			</svg>
			<b>{{file.file.name}}</b>
			<i>{{prettySize}}{{dims}}</i>
			<i v-if="!compact">{{ isDocument
				? t('preview.noThumbnail')
				: t('preview.unavailable') }}</i>
		</span>

		<span class="status-indicator loading-indicator" v-show="file.status == 'loading'">{{ t('preview.inProgress') }}</span>
		<span class="status-indicator success-indicator" v-show="file.status == true">{{ t('preview.uploaded') }}</span>
		<span class="status-indicator failure-indicator" v-show="file.status == false">{{ t('preview.error') }}</span>
	</component>
</template>

<script setup>
import { useUiElements } from "../store/uiElements"
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
const uplImg = ref(null)
const broken = ref(false)

const uiStore = useUiElements()

const props = defineProps({
	file: { type: Object, required: true },
	tag: { type: String, default: 'li' },
	// several files at once: small tiles rather than one big square each
	compact: { type: Boolean, default: false },
	// On a page whose whole judgement is "does that look right yet?", 176px of
	// thumbnail is not enough to judge anything by.
	large: { type: Boolean, default: false },
})

const { t } = useI18n()

defineEmits(['remove'])

const prettySize = computed(() => {
	const b = props.file.size || 0
	if (b < 1024) return b + ' bytes'
	const units = ['Kb', 'MB', 'GB']
	let v = b / 1024
	let i = 0
	while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
	return v.toFixed(1) + ' ' + units[i]
})

/* A document has no dimensions, and saying 0 x 0 about one is a lie.
 *
 * The 0 x 0 below is deliberate FOR AN IMAGE: an image that reports no size
 * has failed to decode, and printing zeroes is how that gets noticed rather
 * than looking like an empty box. A PDF never had a size to report, so the
 * same zeroes describe a failure that did not happen -- and beneath them the
 * fallback also said "preview unavailable on this device", which blames the
 * device for a PDF that no browser was ever going to draw as a thumbnail. */
const isDocument = computed(() => {
	const file = props.file.file
	if (!file) return false
	return file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')
})

/* A REAL PICTURE THIS BROWSER CANNOT READ, which is a third case.
 *
 * HEIC is what every iPhone records and neither Chrome nor Firefox decodes it;
 * Safari does. So "preview unavailable on this device" -- which is wrong for a
 * PDF, because no browser was ever going to draw one -- is exactly right here,
 * and the sentence stays. What does not belong is the 0 x 0 beside it: that
 * exists to make a FAILED DECODE noticeable, and this decode was never going
 * to happen. The file is fine; this browser is not the one that can show it.
 *
 * By extension only, deliberately. `file.type` is the operating system's
 * extension map and is absent often enough that it cannot be the test -- the
 * same reason namedByAttr() exists in lib/task.js. */
const isUnreadableHere = computed(() => {
	const file = props.file.file
	if (!file) return false
	const name = file.name || ''
	const type = (file.type || '').toLowerCase()
	// JPEG XL is here for a NARROWER reason than HEIC, and it costs nothing
	// in the browsers that read it: this whole span is behind `broken`, which
	// only becomes true when the decode has already failed. Chrome 155 and
	// Safari 17 draw a .jxl and never reach it; Firefox does not and lands
	// here, where "preview unavailable on this device" is exactly true and
	// the 0 x 0 beside it is not -- the file decoded fine somewhere else.
	return /\.hei[cf]$/i.test(name) || type.startsWith('image/hei') ||
		/\.jxl$/i.test(name) || type === 'image/jxl'
})

const dims = computed(() => {
	if (isDocument.value || isUnreadableHere.value) return ''
	return props.file.width && props.file.height
		? ` · ${props.file.width} × ${props.file.height}`
		: ' · 0 × 0'
})

function onImgLoad(file) {
	const el = uplImg.value
	if (!el) return
	file.height = el.naturalHeight
	file.width = el.naturalWidth
	// A load event with no pixels behind it is the iOS failure mode.
	broken.value = !el.naturalWidth || !el.naturalHeight
}

function onImgError() {
	broken.value = true
}


</script>

<style scoped>
/* The width is a definite 176px on purpose.

   It was 80%, and that is what kept the previews blank on iOS. The strip
   around it sits in a column flex container with align-items: center, so the
   strip is shrink-to-fit -- its width comes from its contents. A percentage
   width on a child of that is circular, and once the image went out of flow
   there was no intrinsic width left to break the tie, so the box resolved to
   zero.

   It reproduces in Firefox too, but only with the real ancestor chain. A
   harness that gave the strip a fixed-width parent showed nothing wrong, which
   is exactly how this shipped twice.

   px depends on no ancestor at all, and max-width keeps it inside a narrow
   phone, where the box has roughly 272px to work with. */
.file-preview {
	width: 176px;
	max-width: 100%;
	/* auto, not the old 2.5% gutter: that was a gap for the multi-file grid, and it pushes a lone preview off centre */
	margin: 1rem auto;
	position: relative;
	overflow: hidden;
	text-align: center;
}

/* This is what fixed the blank previews on iOS.

   The old rule put aspect-ratio on this box and asked the image for
   height: 100% of it -- while the box was itself a flex item being stretched
   by its line. Three things sizing off each other. Firefox resolves that via
   the ratio, which is why it looked correct everywhere I could test; Safari
   resolves the percentage against an indefinite height, gets auto, and with
   overflow: hidden draws an empty square.

   The files were never the problem: probed on the iPad that showed the bug,
   all three decoded at 1620 x 2160. And the demo thumbnails on the same page
   never broke -- they put the ratio on the img itself, so nothing sizes off
   anything else. That was the tell.

   padding-top resolves against this box's own width, which is definite, and
   the image is taken out of flow, so no percentage of an indefinite parent
   survives anywhere. */
.file-preview.compact {
	width: 104px;
	max-width: 104px;
	margin: 0;
}
.file-preview.compact .fallback { font-size: 10px; padding: 6px; gap: 2px; }
.file-preview.compact .fallback b { font-size: 10px; max-height: 3.2em; }
.file-preview.compact .fallback i { font-size: 9px; }
.file-preview.compact .fallback svg { width: 18px; height: 18px; }

.file-preview::before {
	content: "";
	display: block;
	padding-top: 100%;
}

/* The large preview keeps the picture's own shape.
 *
 * The square box and object-fit: cover are right for a row of thumbnails,
 * where a grid of equal tiles is the point and a crop costs nothing. They are
 * wrong for the one image somebody is judging: a portrait photograph came back
 * cropped to a square, so the scrim looked right on a picture that was not the
 * one being downloaded.
 *
 * The ::before spacer goes with it and the image sizes the box instead, which
 * also sidesteps the Safari trap described above -- nothing here resolves a
 * percentage height against an indefinite one, because no height is declared
 * at all.
 */
.file-preview.large {
	width: fit-content;
	max-width: 100%;
	margin: 0.5rem auto;
}

.file-preview.large::before { display: none; }

.file-preview.large img {
	position: static;
	width: auto;
	height: auto;
	max-width: 100%;
	max-height: 46vh;
	object-fit: contain;
}

/* Big enough to judge by, small enough to leave the slider and the button on
 * the same screen: a 420px square on a 412px phone put the button 180px below
 * the fold, so the loop -- look, drag, press -- needed a scroll in the middle
 * of it. The cap is on the IMAGE now (max-height above) rather than on the
 * box, so a tall photograph is limited by its height and a wide one is not
 * punished for it. Against the viewport rather than a width breakpoint,
 * because what matters is how much room is left. */

/* The scrim and the watermark stamps are styled by WatermarkLayer.vue now.
 * They were styled here, and one of those rules was carrying real weight: the
 * logo needed position: absolute to beat .file-preview.large img setting it
 * static, which is what put every stamp in the top left corner. That fight is
 * over -- these styles are scoped, so they no longer reach inside a child
 * component at all, and the layer declares what it needs for itself. */

.file-preview img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: block;
		object-fit: cover;
}

.file-preview img.unusable { display: none; }

.file-preview .fallback {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 4px;
		padding: 10px;
		box-sizing: border-box;
		background: rgba(255, 255, 255, 0.14);
		border: 1px dashed rgba(255, 255, 255, 0.5);
		border-radius: 12px;
		color: #fff;
		font-size: 12px;
		line-height: 1.3;
		overflow: hidden;
}
.file-preview .fallback b {
		font-size: 12px;
		word-break: break-all;
		max-height: 2.6em;
		overflow: hidden;
}
.file-preview .fallback i { font-style: normal; opacity: 0.8; font-size: 11px; }

.file-preview .close-icon, .status-indicator {
		position: absolute;
		z-index: 2;
		font-family: sans-serif;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 20px;
		border-radius: 20px;
		right: 0.25rem;
		appearance: none;
		border: 0;
		padding: 0;
}

.file-preview .close-icon {
		width: 20px;
		font-size: 20px;
		background: #933;
		color: #fff;
		top: 0.25rem;
		cursor: pointer;
}

.file-preview .status-indicator {
		font-size: 15px;
		bottom: 0.25rem;
		padding: 0 10px;
}

.file-preview .loading-indicator {
		animation: pulse 1.5s linear 0s infinite;
		color: #000;
}

.file-preview .success-indicator {
		background: #6c6;
		color: #040;
}

.file-preview .failure-indicator {
		background: #933;
		color: #fff;
}

@keyframes pulse {
	0% {
		background: #fff;
	}

	50% {
		background: #ddd;
	}

	100% {
		background: #fff;
	}
}
</style>
